import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ExcelJS from "exceljs";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import DateRangeFilter from "../../common/DateRangeFilter";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import useInfoAlert from "../../../hooks/useInfoAlert";
import InfoAlert from "../../common/InfoAlert";
import { useLoader } from "../../../context/LoaderContext";
import { useUserData } from "../../../hooks/useUserData";
import { fetchAssignableStaffLookup } from "../../../services/lookupService";
import {
  fetchDocumentCategories,
  fetchDocumentTypes,
  fetchMyVaultDocuments,
  fetchMyVaultDocumentById,
  vaultCancelCheckOutDocument,
  vaultCheckInDocument,
  vaultCheckOutDocument,
} from "./qmsService";
import { apiMessage, getApiTable, isApiSuccess, normalizeSuccessStoryList } from "./qmsApiUtils";
import { getQmsAccessState } from "./qmsPermissionUtils";
import { getQmsScreen } from "./qmsScreenConfig";
import {
  DEFAULT_VAULT_COLUMNS,
  VAULT_FIELD_OPTIONS,
  getVaultCategoryDisplay,
  getVaultDocumentDisplayName,
  getVaultDocumentListId,
  getVaultExportCellValue,
  getVaultStageClass,
  resolveVaultDocumentStage,
  getVaultTypeDisplay,
  resolveVaultCheckoutReason,
} from "./qmsVaultUtils";
import {
  QMS_COLUMN_DATE_PRESETS,
  QMS_EMPTY_DATE_RANGE,
  matchesQmsDateRange,
} from "./qmsColumnDateFilterUtils";
import QmsMyVaultWorkspaceModal from "./QmsMyVaultWorkspaceModal";
import QmsVaultCheckoutModal from "./QmsVaultCheckoutModal";
import QmsVaultCheckoutReasonModal from "./QmsVaultCheckoutReasonModal";
import QmsVaultUploadModal from "./QmsVaultUploadModal";
import "./QmsPages.scss";

const screen = getQmsScreen("myVault");

const VAULT_TOUR_IDS = {
  root: "qms-vault-table",
  thead: "qms-vault-columns",
  footer: "qms-vault-footer",
  export: "qms-vault-export",
  resetLayout: "qms-vault-reset-layout",
  addColumn: "qms-vault-add-column",
  addColumnField: "DocumentTitle",
  freezeColumn: "qms-vault-freeze-column",
  freezeColumnField: "DocumentTitle",
  dragColumn: "qms-vault-drag-column",
  dragColumnField: "DocumentTitle",
};

const VAULT_CELL_MAX_LEN = 20;
const VAULT_COLUMN_CELL_CLASS = {
  IsCheckOut: "qms-vault__col-checkout",
  CheckOutBy: "qms-vault__col-checkout-by",
};

const VAULT_COLUMN_HEADER_CLASS = {
  IsCheckOut: "qms-vault__header-checkout",
  CheckOutBy: "qms-vault__header-checkout-by",
};

function truncateVaultText(text, maxLen = VAULT_CELL_MAX_LEN) {
  const value = String(text ?? "").trim() || "—";
  if (value === "—" || value.length <= maxLen) return { value, display: value };
  return { value, display: `${value.slice(0, maxLen)}…` };
}

function formatTruncatedCell(text, maxLen = VAULT_CELL_MAX_LEN) {
  const { value, display } = truncateVaultText(text, maxLen);
  return (
    <span className="qms-vault__truncated" title={value}>
      {display}
    </span>
  );
}

function formatTruncatedDocLink(name, onClick, isFirstRow = false) {
  const { value: fullName, display } = truncateVaultText(name || "Document");
  return (
    <button
      type="button"
      className="report-table__cell-link qms-doc-lib__doc-title-link qms-vault__doc-title-link"
      title={fullName}
      data-tour={isFirstRow ? "qms-vault-doc-link" : undefined}
      onClick={onClick}
    >
      {display}
    </button>
  );
}

function VaultCheckoutIconButton({ label, iconClass, tone, onClick }) {
  return (
    <button
      type="button"
      className={`qms-vault__checkout-icon-btn is-${tone}`}
      title={label}
      aria-label={label}
      onClick={onClick}
    >
      <i className={`fa ${iconClass}`} aria-hidden="true" />
    </button>
  );
}

function isGuidLike(value) {
  return /^[0-9a-f-]{36}$/i.test(String(value || "").trim());
}

function resolveStaffDisplay(staffById, value) {
  const raw = String(value || "").trim();
  if (!raw) return "";
  if (isGuidLike(raw)) {
    return staffById.get(raw.toLowerCase())?.StaffName || raw;
  }
  return raw;
}

function isVaultRowCheckedOut(row) {
  return Number(row?.IsCheckOut) === 1;
}

function getVaultCheckoutByKey(row) {
  return String(row?.CheckOutBy || "").trim().toLowerCase();
}

function isQmsHttpSuccess(response) {
  return Number(response?.success) === 1;
}

const QmsMyVault = () => {
  const { userData, loading: userLoading } = useUserData();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const { showLoader, hideLoader } = useLoader();

  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(true);
  const [modifiedDateRange, setModifiedDateRange] = useState(QMS_EMPTY_DATE_RANGE);
  const [checkoutStatusFilter, setCheckoutStatusFilter] = useState("");
  const [checkoutByFilter, setCheckoutByFilter] = useState([]);
  const [refreshToken, setRefreshToken] = useState(0);
  const [staffRows, setStaffRows] = useState([]);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [documentCategories, setDocumentCategories] = useState([]);
  const [typeNameByAbbr, setTypeNameByAbbr] = useState(() => new Map());
  const [categoryNameByAbbr, setCategoryNameByAbbr] = useState(() => new Map());
  const [checkoutModal, setCheckoutModal] = useState({
    open: false,
    mode: "checkout",
    row: null,
  });
  const [checkoutSaving, setCheckoutSaving] = useState(false);
  const [checkoutReasonModal, setCheckoutReasonModal] = useState({
    open: false,
    row: null,
    loading: false,
    details: null,
  });
  const [workspaceModal, setWorkspaceModal] = useState({ open: false, row: null, panel: "preview" });
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadSaving, setUploadSaving] = useState(false);

  const userDataRef = useRef(userData);
  const fetchSeqRef = useRef(0);
  const showAlertRef = useRef(showAlert);
  userDataRef.current = userData;
  showAlertRef.current = showAlert;

  const organizationId = userData?.OrganizationId || "";
  const staffId = userData?.StaffId || "";

  const isSystemAdmin = userData?.Role === "System Administrator";
  const access = getQmsAccessState({
    userData,
    isSystemAdmin,
    permissionKey: screen?.permissionKey || "QMS_VAULT",
    loading: userLoading,
  });

  const staffById = useMemo(() => {
    const map = new Map();
    staffRows.forEach((staff) => {
      const id = String(staff?.StaffId || "").trim().toLowerCase();
      if (id) map.set(id, staff);
    });
    return map;
  }, [staffRows]);

  useEffect(() => {
    if (access.status !== "allowed" || !organizationId || !staffId) {
      return undefined;
    }

    let cancelled = false;
    Promise.all([
      fetchAssignableStaffLookup(userDataRef.current),
      fetchDocumentTypes(userDataRef.current),
      fetchDocumentCategories(userDataRef.current),
    ])
      .then(([staff, typeRes, catRes]) => {
        if (cancelled) return;
        setStaffRows(Array.isArray(staff) ? staff : []);

        const types = isQmsHttpSuccess(typeRes)
          ? normalizeSuccessStoryList(typeRes).map((row) => ({
              id: String(row.DocumentTypeId),
              label: row.DocumentType,
              abbr: row.Abberiviation,
            }))
          : [];
        setDocumentTypes(types);

        const typeMap = new Map();
        types.forEach((row) => {
          if (row.abbr) typeMap.set(row.abbr, row.label || row.abbr);
        });
        setTypeNameByAbbr(typeMap);

        const categories = isQmsHttpSuccess(catRes)
          ? normalizeSuccessStoryList(catRes).map((row) => ({
              id: String(row.DocumentCategoryId),
              label: row.DocumentCategory,
              abbr: row.Abberiviation,
            }))
          : [];
        setDocumentCategories(categories);

        const catMap = new Map();
        categories.forEach((row) => {
          if (row.abbr) catMap.set(row.abbr, row.label || row.abbr);
        });
        setCategoryNameByAbbr(catMap);
      })
      .catch(() => {
        if (!cancelled) {
          setStaffRows([]);
          setDocumentTypes([]);
          setDocumentCategories([]);
          setTypeNameByAbbr(new Map());
          setCategoryNameByAbbr(new Map());
        }
      });

    return () => {
      cancelled = true;
    };
  }, [access.status, organizationId, staffId]);

  const loadDocuments = useCallback(async () => {
    if (access.status !== "allowed" || !organizationId || !staffId) {
      return;
    }

    const seq = ++fetchSeqRef.current;
    setDocumentsLoading(true);

    try {
      const response = await fetchMyVaultDocuments(userDataRef.current);
      if (seq !== fetchSeqRef.current) return;
      if (!isApiSuccess(response)) {
        throw new Error(apiMessage(response) || "Failed to load My Vault documents.");
      }
      setDocuments(getApiTable(response, "DMS_MyVault_Documents"));
    } catch (error) {
      if (seq !== fetchSeqRef.current) return;
      showAlertRef.current({
        title: "Error",
        message: error?.message || "Failed to load My Vault documents.",
        type: "danger",
      });
      setDocuments([]);
    } finally {
      if (seq === fetchSeqRef.current) {
        setDocumentsLoading(false);
      }
    }
  }, [access.status, organizationId, staffId]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments, refreshToken]);

  const handleRefresh = useCallback(() => {
    loadDocuments();
  }, [loadDocuments]);

  const breadcrumbActions = useMemo(
    () => [
      {
        icon: "fas fa-upload",
        label: "Upload",
        onClick: () => setUploadModalOpen(true),
      },
    ],
    [],
  );

  const breadcrumbToolbarExtras = useMemo(
    () => (
      <button
        type="button"
        className="actions-trigger"
        onClick={handleRefresh}
        disabled={documentsLoading}
        aria-busy={documentsLoading}
      >
        <i
          className={`fas fa-sync-alt me-1${documentsLoading ? " fa-spin" : ""}`}
          aria-hidden="true"
        />
        Refresh
      </button>
    ),
    [documentsLoading, handleRefresh],
  );

  const openDocumentWorkspace = useCallback((row, panel = "preview") => {
    setWorkspaceModal({ open: true, row, panel });
  }, []);

  const closeDocumentWorkspace = useCallback(() => {
    setWorkspaceModal({ open: false, row: null, panel: "preview" });
  }, []);

  const openCheckoutModal = useCallback((row, mode) => {
    setCheckoutModal({ open: true, mode, row });
  }, []);

  const closeCheckoutReasonModal = useCallback(() => {
    setCheckoutReasonModal({ open: false, row: null, loading: false, details: null });
  }, []);

  const openCheckoutReasonModal = useCallback(async (row) => {
    setCheckoutReasonModal({ open: true, row, loading: true, details: null });
    try {
      const response = await fetchMyVaultDocumentById(
        userDataRef.current,
        row.DocumentId,
      );
      setCheckoutReasonModal({
        open: true,
        row,
        loading: false,
        details: resolveVaultCheckoutReason(response),
      });
    } catch (error) {
      closeCheckoutReasonModal();
      showAlertRef.current({
        title: "Checkout reason",
        message: error?.message || "Could not load checkout details.",
        type: "danger",
      });
    }
  }, [closeCheckoutReasonModal]);

  const closeCheckoutModal = useCallback(() => {
    if (checkoutSaving) return;
    setCheckoutModal({ open: false, mode: "checkout", row: null });
  }, [checkoutSaving]);

  const handleCheckoutConfirm = useCallback(
    async (payload) => {
      setCheckoutSaving(true);
      try {
        let response;
        if (payload.mode === "checkout") {
          response = await vaultCheckOutDocument(userDataRef.current, {
            documentId: payload.documentId,
            comments: payload.comments,
          });
        } else if (payload.mode === "cancel") {
          response = await vaultCancelCheckOutDocument(userDataRef.current, {
            documentId: payload.documentId,
            comments: payload.comments,
          });
        } else {
          response = await vaultCheckInDocument(userDataRef.current, {
            SelectedDocumentId: payload.selectedDocumentId,
            Comments: payload.comments,
            DocumentExtension: payload.documentExtension,
            DocumentSize: payload.documentSize,
            DocumentBase64: payload.documentBase64,
            DocumentTitle: payload.documentTitle,
            DocumentMetadataJson: payload.documentMetadataJson,
          });
        }

        if (!isApiSuccess(response)) {
          throw new Error(apiMessage(response) || "Request failed.");
        }

        showAlertRef.current({
          title: "Success",
          message: apiMessage(response) || "Document updated.",
          type: "success",
        });
        setCheckoutModal({ open: false, mode: "checkout", row: null });
        setRefreshToken((token) => token + 1);
        loadDocuments();
      } catch (error) {
        showAlertRef.current({
          title: "Error",
          message: error?.message || "Could not update check-out status.",
          type: "danger",
        });
      } finally {
        setCheckoutSaving(false);
      }
    },
    [loadDocuments],
  );

  const renderCheckoutActions = useCallback(
    (row) => {
      const isCheckedOut = Number(row.IsCheckOut) === 1;
      const checkoutStaffId = String(row.CheckOutBy || "").trim();
      const isOwnCheckout =
        isCheckedOut &&
        checkoutStaffId &&
        checkoutStaffId.toLowerCase() === staffId.toLowerCase();

      const reasonButton = (
        <VaultCheckoutIconButton
          label="View checkout reason"
          iconClass="fas fa-info-circle"
          tone="info"
          onClick={() => openCheckoutReasonModal(row)}
        />
      );

      if (!isCheckedOut) {
        return (
          <div className="qms-vault__checkout-actions">
            <VaultCheckoutIconButton
              label="Check out"
              iconClass="fas fa-sign-out-alt"
              tone="checkout"
              onClick={() => openCheckoutModal(row, "checkout")}
            />
          </div>
        );
      }

      if (isOwnCheckout) {
        return (
          <div className="qms-vault__checkout-actions">
            <VaultCheckoutIconButton
              label="Check in"
              iconClass="fas fa-sign-in-alt"
              tone="checkin"
              onClick={() => openCheckoutModal(row, "checkin")}
            />
            <VaultCheckoutIconButton
              label="Cancel checkout"
              iconClass="fas fa-times"
              tone="cancel"
              onClick={() => openCheckoutModal(row, "cancel")}
            />
            {reasonButton}
          </div>
        );
      }

      return (
        <div className="qms-vault__checkout-actions">
          <span className="qms-vault__checkout-locked" title="Checked out by another user">
            <i className="fa fas fa-lock" aria-hidden="true" />
            <span className="visually-hidden">In use</span>
          </span>
          {reasonButton}
        </div>
      );
    },
    [openCheckoutModal, openCheckoutReasonModal, staffId],
  );

  const checkoutByFilterOptions = useMemo(() => {
    const map = new Map();
    (documents || []).forEach((row) => {
      if (!isVaultRowCheckedOut(row)) return;
      const raw = String(row.CheckOutBy || "").trim();
      if (!raw) return;
      const key = raw.toLowerCase();
      if (map.has(key)) return;
      map.set(key, {
        value: key,
        label: resolveStaffDisplay(staffById, raw) || raw,
      });
    });
    return [...map.values()].sort((a, b) =>
      String(a.label).localeCompare(String(b.label)),
    );
  }, [documents, staffById]);

  const tableRows = useMemo(() => {
    const selectedCheckoutBy = checkoutByFilter.length
      ? new Set(checkoutByFilter.map((value) => String(value).toLowerCase()))
      : null;
    return (documents || []).filter((row) => {
      if (!matchesQmsDateRange(row?.SystemDateTime, modifiedDateRange)) {
        return false;
      }
      if (checkoutStatusFilter === "1" && !isVaultRowCheckedOut(row)) {
        return false;
      }
      if (checkoutStatusFilter === "0" && isVaultRowCheckedOut(row)) {
        return false;
      }
      if (selectedCheckoutBy) {
        if (!isVaultRowCheckedOut(row)) return false;
        if (!selectedCheckoutBy.has(getVaultCheckoutByKey(row))) return false;
      }
      return true;
    });
  }, [checkoutByFilter, checkoutStatusFilter, documents, modifiedDateRange]);

  const firstRowDocumentId = tableRows[0]?.DocumentId;

  const renderReportCell = useCallback(
    (row, field) => {
      if (field === "DocumentId") {
        const docId = getVaultDocumentListId(row);
        return (
          <span className="qms-doc-lib__doc-id" title={docId}>
            {docId}
          </span>
        );
      }
      if (field === "DocumentTitle") {
        const name = getVaultDocumentDisplayName(row);
        const isFirstRow = row.DocumentId === firstRowDocumentId;
        return formatTruncatedDocLink(name, () => openDocumentWorkspace(row, "preview"), isFirstRow);
      }
      if (field === "DocumentType") {
        return formatTruncatedCell(getVaultTypeDisplay(row, typeNameByAbbr));
      }
      if (field === "DocumentCategoryAbberiviation") {
        return formatTruncatedCell(getVaultCategoryDisplay(row, categoryNameByAbbr));
      }
      if (field === "VersionNumber") {
        return formatTruncatedCell(row.VersionNumber ?? row.versionNumber ?? "—");
      }
      if (field === "DocumentStage") {
        const stage = resolveVaultDocumentStage(row.DocumentStage);
        const { value, display } = truncateVaultText(stage);
        return (
          <span
            className={`qms-vault__stage ${getVaultStageClass(stage)}`}
            title={value}
          >
            {display}
          </span>
        );
      }
      if (field === "CreatedBy") {
        const ownerName = resolveStaffDisplay(staffById, row.CreatedBy);
        return ownerName ? formatTruncatedCell(ownerName) : "—";
      }
      if (field === "IsCheckOut") {
        return renderCheckoutActions(row);
      }
      if (field === "CheckOutBy") {
        const isCheckedOut = Number(row.IsCheckOut) === 1;
        if (!isCheckedOut) return "—";
        const checkoutName = resolveStaffDisplay(staffById, row.CheckOutBy);
        return formatTruncatedCell(checkoutName || "Unknown");
      }
      return formatTruncatedCell(row[field]);
    },
    [
      firstRowDocumentId,
      openCheckoutModal,
      openDocumentWorkspace,
      renderCheckoutActions,
      staffById,
      staffId,
      typeNameByAbbr,
      categoryNameByAbbr,
    ],
  );

  const vaultMatchColumnSearch = useCallback(
    (row, field, searchValue) => {
      const raw = String(searchValue || "").trim();
      if (!raw) return true;

      if (field === "DocumentId") {
        const listId = getVaultDocumentListId(row).toLowerCase();
        const term = raw.toLowerCase();
        const digits = term.replace(/\D/g, "");
        if (digits) {
          return listId.replace(/\D/g, "").includes(digits) || listId.includes(term);
        }
        return listId.includes(term);
      }

      if (field === "DocumentTitle") {
        return getVaultDocumentDisplayName(row).toLowerCase().includes(raw.toLowerCase());
      }

      if (field === "DocumentType") {
        const type = documentTypes.find((item) => String(item.id) === raw);
        if (!type) return true;
        return (row.DocumentTypeAbberiviation || "") === type.abbr;
      }

      if (field === "DocumentCategoryAbberiviation") {
        const category = documentCategories.find((item) => String(item.id) === raw);
        if (!category) return true;
        const rowCat =
          row.DocumentCategoryAbberiviation || row.DocumentCategoryAbbreviation || "";
        return rowCat === category.abbr;
      }

      if (field === "CreatedBy" || field === "CheckOutBy" || field === "IsCheckOut") {
        return true;
      }

      if (field === "SystemDateTime") {
        return true;
      }

      return String(row[field] ?? "")
        .toLowerCase()
        .includes(raw.toLowerCase());
    },
    [documentCategories, documentTypes],
  );

  const renderColumnSearch = useCallback(
    (field, options) => {
      if (field === "DocumentId") {
        if (options?.checkOnly) return true;
        return (
          <input
            type="search"
            className="report-table__column-search qms-doc-lib__column-filter"
            placeholder="Search ID"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          />
        );
      }

      if (field === "DocumentType") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          >
            <option value="">All types</option>
            {documentTypes.map((type) => (
              <option key={type.id} value={type.id}>
                {type.abbr ? `${type.abbr} - ${type.label}` : type.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "DocumentCategoryAbberiviation") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={options?.value || ""}
            disabled={options?.disabled}
            onChange={(event) => options?.onChange?.(event.target.value)}
          >
            <option value="">All categories</option>
            {documentCategories.map((category) => (
              <option key={category.id} value={category.id}>
                {category.abbr ? `${category.abbr} - ${category.label}` : category.label}
              </option>
            ))}
          </select>
        );
      }

      if (field === "CreatedBy") {
        if (options?.checkOnly) return true;
        return <span className="report-table__column-search report-table__column-search--blank" />;
      }

      if (field === "IsCheckOut") {
        if (options?.checkOnly) return true;
        return (
          <select
            className="report-table__column-search qms-doc-lib__column-filter qms-doc-lib__column-select"
            value={checkoutStatusFilter}
            disabled={options?.disabled}
            onChange={(event) => setCheckoutStatusFilter(event.target.value)}
            aria-label="Filter by checkout status"
          >
            <option value="">All</option>
            <option value="1">Checked out</option>
            <option value="0">Not checked out</option>
          </select>
        );
      }

      if (field === "CheckOutBy") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-filter-multiselect">
            <BadgeMultiSelect
              options={checkoutByFilterOptions}
              value={checkoutByFilter}
              onChange={setCheckoutByFilter}
              emptyLabel="All people"
              addLabel="+"
              popoverTitle="Filter by checkout by"
              popoverSubtitle="Select one or more people"
              showSearch
              showSelectAll
              compactSummary
              disabled={options?.disabled}
            />
          </div>
        );
      }

      if (field === "SystemDateTime") {
        if (options?.checkOnly) return true;
        return (
          <div className="qms-doc-lib__column-date-filter">
            <DateRangeFilter
              value={modifiedDateRange}
              onChange={setModifiedDateRange}
              placeholder="Modified range"
              presetOptions={QMS_COLUMN_DATE_PRESETS}
              compact
            />
          </div>
        );
      }

      return null;
    },
    [
      checkoutByFilter,
      checkoutByFilterOptions,
      checkoutStatusFilter,
      documentCategories,
      documentTypes,
      modifiedDateRange,
    ],
  );

  const getColumnCellClassName = useCallback(
    (field) => VAULT_COLUMN_CELL_CLASS[field] || "",
    [],
  );

  const getColumnHeaderClassName = useCallback(
    (field) => VAULT_COLUMN_HEADER_CLASS[field] || "",
    [],
  );

  const getVaultFieldLabel = useCallback((field) => {
    if (field === "IsCheckOut") return "Checkout";
    return VAULT_FIELD_OPTIONS.find((x) => x.key === field)?.label || field;
  }, []);

  const layoutStorageKey = useMemo(
    () => (staffId ? `qms-vault-layout-v5-${staffId}` : ""),
    [staffId],
  );

  const handleExportToExcel = useCallback(
    async ({ columns }) => {
      if (!documents.length) {
        showAlert({
          title: "Export",
          message: "No documents to export.",
          type: "info",
        });
        return;
      }

      const exportColumns = columns || DEFAULT_VAULT_COLUMNS;
      if (!exportColumns.length) {
        showAlert({
          title: "Export",
          message: "Add at least one data column before exporting.",
          type: "info",
        });
        return;
      }

      showLoader("Preparing Excel export...");
      try {
        const headers = exportColumns.map(
          (field) =>
            VAULT_FIELD_OPTIONS.find((x) => x.key === field)?.label || field,
        );

        const wb = new ExcelJS.Workbook();
        const ws = wb.addWorksheet("My Vault");
        const headerRow = ws.addRow(headers);
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "FF4472C4" },
          };
        });

        documents.forEach((row) => {
          ws.addRow(
            exportColumns.map((field) => {
              if (field === "CheckOutBy") {
                return resolveStaffDisplay(staffById, row.CheckOutBy);
              }
              if (field === "CreatedBy") {
                return resolveStaffDisplay(staffById, row.CreatedBy);
              }
              return getVaultExportCellValue(row, field, {
                typeNameByAbbr,
                categoryNameByAbbr,
              });
            }),
          );
        });

        ws.columns = headers.map((header) => ({
          header,
          key: header,
          width: Math.max(14, header.length + 2),
        }));

        const buf = await wb.xlsx.writeBuffer();
        const blob = new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute(
          "download",
          `My_Vault_${new Date().toISOString().split("T")[0]}.xlsx`,
        );
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("My Vault export failed", error);
        showAlert({
          title: "Export failed",
          message: error?.message || "Could not export My Vault documents.",
          type: "danger",
        });
      } finally {
        hideLoader();
      }
    },
    [documents, hideLoader, showAlert, showLoader, staffById, typeNameByAbbr, categoryNameByAbbr],
  );

  if (access.status === "loading") {
    return (
      <div className="user-mgmt-page qms-my-vault qms-document-library">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: screen?.title || "My Vault" },
          ]}
        />
        <p className="text-muted mb-0">Loading…</p>
      </div>
    );
  }

  if (access.status === "denied") {
    return (
      <div className="user-mgmt-page qms-my-vault qms-document-library">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: screen?.title || "My Vault" },
          ]}
        />
        <div className="alert alert-warning mt-3" role="alert">
          <strong>Access restricted.</strong> {access.message}
        </div>
      </div>
    );
  }

  return (
    <div className="user-mgmt-page qms-my-vault qms-document-library">
      <InfoAlert isOpen={alertState.isOpen} onClose={hideAlert} {...alertState} />
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: screen?.title || "My Vault" },
        ]}
        actions={breadcrumbActions}
        toolbarExtras={breadcrumbToolbarExtras}
      />

      <div className="qms-doc-lib__panel">
        <div className="qms-doc-lib__report-wrap">
          <ReportTable
            rows={tableRows}
            fieldOptions={VAULT_FIELD_OPTIONS}
            defaultColumns={DEFAULT_VAULT_COLUMNS}
            getFieldLabel={getVaultFieldLabel}
            renderCell={renderReportCell}
            renderColumnSearch={renderColumnSearch}
            matchColumnSearch={vaultMatchColumnSearch}
            getColumnCellClassName={getColumnCellClassName}
            getColumnHeaderClassName={getColumnHeaderClassName}
            hideColumnHeaderActionsFields={["IsCheckOut", "CheckOutBy"]}
            pageSizeOptions={[10, 25, 50, 100]}
            columnSearchMode="client"
            persistLayoutKey={layoutStorageKey}
            onExportExcel={handleExportToExcel}
            exportLabel="Export Excel"
            tourIds={VAULT_TOUR_IDS}
            loading={documentsLoading}
          />
        </div>
      </div>

      <QmsVaultUploadModal
        open={uploadModalOpen}
        userData={userData}
        documentTypes={documentTypes}
        documentCategories={documentCategories}
        saving={uploadSaving}
        onSavingChange={setUploadSaving}
        onClose={() => {
          if (!uploadSaving) setUploadModalOpen(false);
        }}
        onNotify={showAlertRef.current}
        onUploaded={() => {
          setRefreshToken((token) => token + 1);
          loadDocuments();
        }}
      />

      <QmsVaultCheckoutModal
        open={checkoutModal.open}
        mode={checkoutModal.mode}
        row={checkoutModal.row}
        userData={userData}
        saving={checkoutSaving}
        onClose={closeCheckoutModal}
        onConfirm={handleCheckoutConfirm}
      />

      <QmsVaultCheckoutReasonModal
        open={checkoutReasonModal.open}
        row={checkoutReasonModal.row}
        loading={checkoutReasonModal.loading}
        details={checkoutReasonModal.details}
        onClose={closeCheckoutReasonModal}
      />

      <QmsMyVaultWorkspaceModal
        open={workspaceModal.open}
        row={workspaceModal.row}
        userData={userData}
        activePanel={workspaceModal.panel}
        onPanelChange={(panel) =>
          setWorkspaceModal((prev) => ({ ...prev, panel }))
        }
        onClose={closeDocumentWorkspace}
        onNotify={showAlertRef.current}
        onDocumentUpdated={() => setRefreshToken((token) => token + 1)}
      />
    </div>
  );
};

export default QmsMyVault;
